import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, Mail, Building2, Calendar, Wallet, Inbox, ExternalLink } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Loading({ label }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505]">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 animate-pulse">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      axios
        .get(`${API}/admin/leads`, { withCredentials: true })
        .then((r) => setLeads(r.data))
        .catch(() => setLeads([]));
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (loading || !user) return <Loading label="Loading dashboard…" />;

  if (!user.is_admin) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#050505] px-6 text-center" data-testid="admin-forbidden">
        <div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tighter">Not authorized</h1>
          <p className="mt-4 max-w-sm text-sm font-light text-white/55">
            The account <span className="text-white">{user.email}</span> isn't on the
            approved team list. Contact an administrator for access.
          </p>
        </div>
        <button
          data-testid="forbidden-logout-btn"
          onClick={handleLogout}
          className="rounded-full border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors hover:bg-white hover:text-black"
        >
          Sign out
        </button>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="font-display text-lg font-bold uppercase tracking-tight">Visions</span>
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            {user.picture && (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-white/20" />
            )}
            <span className="hidden font-mono text-xs text-white/60 sm:block">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:bg-[#FF3B30] hover:border-[#FF3B30]"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-12 md:px-10 md:py-16">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="mb-3 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FF3B30]" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">Quote Requests</span>
            </div>
            <h1 className="font-display text-4xl font-bold uppercase tracking-tighter md:text-5xl">Leads</h1>
          </div>
          <div className="text-right">
            <div className="font-mono text-4xl font-bold md:text-5xl">
              {leads === null ? "…" : leads.length}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Total</div>
          </div>
        </div>

        {leads === null ? (
          <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.3em] text-white/40 animate-pulse">
            Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-white/10 py-24 text-center">
            <Inbox className="h-10 w-10 text-white/30" strokeWidth={1.4} />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">No leads yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {leads.map((lead, i) => (
              <motion.div
                key={lead.id}
                data-testid={`lead-card-${lead.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.4) }}
                className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 transition-colors hover:border-white/25"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-semibold tracking-tight">{lead.name}</h3>
                    {lead.company && (
                      <div className="mt-1 flex items-center gap-1.5 font-mono text-xs text-white/50">
                        <Building2 className="h-3.5 w-3.5" /> {lead.company}
                      </div>
                    )}
                  </div>
                  <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {lead.service && (
                    <span className="rounded-full bg-[#FF3B30]/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[#FF6259]">
                      {lead.service}
                    </span>
                  )}
                  {lead.budget && (
                    <span className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-white/60">
                      <Wallet className="h-3 w-3" /> {lead.budget}
                    </span>
                  )}
                  {lead.event_date && (
                    <span className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-white/60">
                      <Calendar className="h-3 w-3" /> {lead.event_date}
                    </span>
                  )}
                </div>

                <p className="mt-4 text-sm font-light leading-relaxed text-white/70">{lead.message}</p>

                <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-1.5 font-mono text-xs text-white/60 transition-colors hover:text-white"
                  >
                    <Mail className="h-3.5 w-3.5" /> {lead.email}
                  </a>
                  <a
                    href={`mailto:${lead.email}?subject=Re: Your Visions Displays quote request`}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-[#FF6259] transition-colors hover:text-white"
                  >
                    Reply <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
